import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { IssueMap, ReportCategory } from "../types";
import { OWNER_COLORS, SECTION_TITLE, ownerForCategory, slugify } from "./config";

function prettify(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function SeverityBar({ critMaj, totalAll }: { critMaj: number; totalAll: number }) {
  if (totalAll === 0) return null;
  const pct = Math.round((critMaj / totalAll) * 100);
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="h-2 w-24 rounded-full bg-muted overflow-hidden flex">
        <div className="h-full bg-red-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function OwnerBadge({ owner }: { owner: string }) {
  const colors = OWNER_COLORS[owner] ?? OWNER_COLORS["Triage"]!;
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${colors.bg} ${colors.text}`}>
      {owner}
    </span>
  );
}

function CategoryRow({
  category,
  issueMap,
  catStats,
  totalCritMajAll,
}: {
  category: ReportCategory;
  issueMap: IssueMap;
  catStats: { critMaj: number; total: number };
  totalCritMajAll: number;
}) {
  const [open, setOpen] = useState(false);

  const owner = ownerForCategory(category.name);

  const subcats = category.subcategories
    .map((sub) => {
      const critMaj = sub.issue_ids.filter((id) => {
        const sev = issueMap[id]?.severity;
        return sev === "critical" || sev === "major";
      }).length;
      return { name: sub.name, total: sub.issue_ids.length, critMaj };
    })
    .sort((a, b) => b.critMaj - a.critMaj);

  const { critMaj, total } = catStats;
  const critMajPct = totalCritMajAll > 0 ? Math.round((critMaj / totalCritMajAll) * 100) : 0;

  return (
    <div id={`summary__${slugify(category.name)}`} className="rounded-xl border bg-card overflow-hidden scroll-mt-6">
      {/* Category header row */}
      <button
        type="button"
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-accent/40 transition-colors text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="text-sm font-semibold text-foreground min-w-[220px]">
          {prettify(category.name)}
        </span>
        <OwnerBadge owner={owner} />
        <span className="text-sm font-bold tabular-nums text-foreground ml-2">
          {critMaj}
        </span>
        <span className="text-sm text-muted-foreground tabular-nums">
          crit/maj / {total} total
        </span>
        {/* Bar: crit/maj as fraction of this category's total */}
        <div className="flex-1 min-w-[80px] max-w-[200px]">
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${critMajPct}%`,
                background: `linear-gradient(to right, #ef4444, #f97316)`,
              }}
            />
          </div>
        </div>
        <div className="ml-auto shrink-0 text-muted-foreground">
          {open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </div>
      </button>

      {/* Expanded subcategory table */}
      {open && (
        <div className="border-t">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/30">
                <th className="px-5 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Subcategory
                </th>
                <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-24">
                  Crit/Maj
                </th>
                <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-20">
                  Total
                </th>
                <th className="px-5 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-32">
                  Crit/Maj Share
                </th>
              </tr>
            </thead>
            <tbody>
              {subcats.map((sub, i) => (
                <tr key={i} id={`summary__${slugify(category.name)}__${slugify(sub.name)}`} className="border-t hover:bg-muted/20 transition-colors">
                  <td className="px-5 py-2.5 text-sm">
                    <a
                      href={`#${slugify(category.name)}__${slugify(sub.name)}`}
                      className="text-foreground hover:text-blue-400 hover:underline transition-colors"
                    >
                      {sub.name}
                    </a>
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold tabular-nums">{sub.critMaj}</td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground tabular-nums">{sub.total}</td>
                  <td className="px-5 py-2.5">
                    <SeverityBar critMaj={sub.critMaj} totalAll={totalCritMajAll} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function CategorySummary({
  categories,
  issueMap,
  catStatsMap,
}: {
  categories: ReportCategory[];
  issueMap: IssueMap;
  catStatsMap: Map<string, { critMaj: number; total: number }>;
}) {
  const sorted = [...categories].sort(
    (a, b) => (catStatsMap.get(b.name)?.critMaj ?? 0) - (catStatsMap.get(a.name)?.critMaj ?? 0),
  );

  const totalCritMajAll = [...catStatsMap.values()].reduce((sum, s) => sum + s.critMaj, 0);

  return (
    <div>
      <h2 className={`${SECTION_TITLE} mb-1`}>
        Where the Problems Live
      </h2>
      <p className="text-xs text-muted-foreground mb-3">
        Bar shows each category's share of all critical/major issues in this report.
      </p>
      <div className="flex flex-col gap-2">
        {sorted.map((cat, i) => (
          <CategoryRow
            key={i}
            category={cat}
            issueMap={issueMap}
            catStats={catStatsMap.get(cat.name) ?? { critMaj: 0, total: 0 }}
            totalCritMajAll={totalCritMajAll}
          />
        ))}
      </div>
    </div>
  );
}
